/**
 * Shadow Tokens for EroChat Design System
 *
 * Optimized for dark backgrounds - uses lighter shadow colors
 * and subtle glow effects for better visibility.
 *
 * @module design-system/tokens/shadows
 */

/**
 * Shadow token values interface
 */
export interface ShadowTokens {
  /** No shadow */
  none: string;
  /** Small shadow - subtle depth for small elements */
  sm: string;
  /** Medium shadow - standard depth for cards and containers */
  md: string;
  /** Large shadow - elevated elements like dropdowns */
  lg: string;
  /** Extra large shadow - highly elevated elements */
  xl: string;
  /** Inner shadow - inset depth effect */
  inner: string;
  /** Card shadow - semantic alias for card components */
  card: string;
  /** Dropdown shadow - semantic alias for dropdown menus */
  dropdown: string;
  /** Modal shadow - semantic alias for modal dialogs */
  modal: string;
  /** Glow effect - subtle glow for interactive states on dark backgrounds */
  glow: string;
  /** Primary glow - brand-colored glow effect */
  glowPrimary: string;
}

const SHADOW_TOKEN_KEYS: Array<keyof ShadowTokens> = [
  'none',
  'sm',
  'md',
  'lg',
  'xl',
  'inner',
  'card',
  'dropdown',
  'modal',
  'glow',
  'glowPrimary',
];

/**
 * Default shadow values optimized for dark backgrounds.
 *
 * Uses higher opacity and subtle white/colored highlights
 * to ensure shadows are visible on dark themes.
 *
 * @example
 * ```tsx
 * import { defaultShadows } from '@/design-system/tokens/shadows';
 *
 * const Card = () => (
 *   <div style={{ boxShadow: defaultShadows.card }}>
 *     Content
 *   </div>
 * );
 * ```
 */
export const defaultShadows: ShadowTokens = {
  /** No shadow */
  none: 'none',

  /**
   * Small shadow - subtle depth
   * Best for: small buttons, chips, badges
   */
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.3), 0 1px 3px 0 rgb(0 0 0 / 0.2)',

  /**
   * Medium shadow - standard depth
   * Best for: cards, panels, form elements
   */
  md: '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3)',

  /**
   * Large shadow - elevated depth
   * Best for: dropdowns, popovers, floating elements
   */
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.5), 0 4px 6px -4px rgb(0 0 0 / 0.4)',

  /**
   * Extra large shadow - high elevation
   * Best for: modals, dialogs, overlays
   */
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.6), 0 8px 10px -6px rgb(0 0 0 / 0.5)',

  /**
   * Inner shadow - inset depth
   * Best for: input fields, pressed states
   */
  inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.3)',

  /**
   * Card shadow - semantic token for card components
   * Includes subtle top highlight for dark backgrounds
   */
  card: '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3), inset 0 1px 0 0 rgb(255 255 255 / 0.05)',

  /**
   * Dropdown shadow - semantic token for dropdown menus
   * Stronger shadow for clear separation from content
   */
  dropdown: '0 10px 15px -3px rgb(0 0 0 / 0.5), 0 4px 6px -4px rgb(0 0 0 / 0.4), 0 0 0 1px rgb(255 255 255 / 0.05)',

  /**
   * Modal shadow - semantic token for modal dialogs
   * Maximum elevation with subtle outer glow
   */
  modal: '0 25px 50px -12px rgb(0 0 0 / 0.7), 0 0 0 1px rgb(255 255 255 / 0.1)',

  /**
   * Glow effect - subtle glow for interactive states
   * Best for: hover states, focus rings on dark backgrounds
   */
  glow: '0 0 15px 0 rgb(255 255 255 / 0.1)',

  /**
   * Primary glow - brand-colored glow
   * Best for: active states, primary actions
   * Uses pink/magenta tones matching EroChat brand
   */
  glowPrimary: '0 0 20px 0 rgb(236 72 153 / 0.4)',
};

/**
 * Helper to get a shadow CSS variable reference
 *
 * @param key - Shadow token key
 * @returns CSS variable reference string
 *
 * @example
 * ```tsx
 * const shadow = getShadowVar('card');
 * // Returns: 'var(--shadow-card)'
 * ```
 */
export const getShadowVar = (key: keyof ShadowTokens): string => {
  return `var(--shadow-${key})`;
};

/**
 * Converts shadow tokens to CSS custom properties
 *
 * @param shadows - Shadow tokens object
 * @returns Object with CSS variable names as keys
 *
 * @example
 * ```ts
 * const cssVars = shadowsToCssVars(defaultShadows);
 * // { '--shadow-none': 'none', '--shadow-sm': '...', ... }
 * ```
 */
export const shadowsToCssVars = (shadows: ShadowTokens): Record<string, string> => {
  const cssVariables: Record<string, string> = {};
  for (const key of SHADOW_TOKEN_KEYS) {
    cssVariables[`--shadow-${key}`] = shadows[key];
  }
  return cssVariables;
};
